import { BranchLocation } from '@model/branch-location';
import { Storage } from '@service/storage';
import { Events, pubsub } from '@service/pubsub';
import logger from '@service/logger';

export class ZipCodeModal extends HTMLElement {
  constructor() {
    super();
    this.storage = Storage.sessionForKey('locationInformation');
    this.currentLocation = new BranchLocation();
    this.raised = false;
  }

  connectedCallback() {
    // Get modal configuration from attributes
    this.modalId = this.getAttribute('modal-id') || 'zipcode-modal';
    this.modalContext = this.getAttribute('modal-context') || 'entry';
    this.autoShow = this.hasAttribute('auto-show');

    // Find existing DOM elements within this custom element
    this.init();
  }

  init() {
    // Find DOM elements that already exist in the Liquid template
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('input[data-zipcode-input]');
    this.form = this.querySelector('[data-zipcode-form]');
    this.errorMessage = this.querySelector('[data-error-message]');
    this.submitButton = this.querySelector('[data-submit-zipcode]');
    this.closeButtons = this.querySelectorAll('[data-modal-close]');

    if (!this.dialog) {
      logger.error('ZIP Code Modal: Dialog element not found within custom element');
      return;
    }

    // Subscribe to pubsub events
    pubsub.subscribe(Events.raiseZipCodeModal, () => {
      this.show();
    });

    // Set up event listeners on existing elements
    this.setupEventListeners();

    // Auto-show modal if configured and conditions are met
    if (this.autoShow && this.shouldShowModal()) {
      setTimeout(() => {
        this.show();
      }, 2000);
    }
  }

  setupEventListeners() {
    // Form submission
    this.form?.addEventListener('submit', e => {
      e.preventDefault();
      this.handleSubmit().then(() => {});
    });

    // Input formatting and validation
    this.input?.addEventListener('input', () => {
      this.formatInput();
      this.clearError();
    });

    // Enter key handling
    this.input?.addEventListener('keypress', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit().then(() => {});
      }
    });

    // Close button handlers
    this.closeButtons?.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Submit button click
    this.submitButton?.addEventListener('click', e => {
      e.preventDefault();
      this.handleSubmit().then(() => {});
    });

    // Dialog close event (handles ESC key and click outside)
    this.dialog?.addEventListener('close', () => {
      this.onDialogClose();
    });

    // Handle backdrop click (click outside dialog)
    this.dialog?.addEventListener('click', e => {
      if (e.target === this.dialog) {
        this.close();
      }
    });
  }

  // Public API methods
  show() {
    if (this.raised || !this.dialog) return;

    this.reset();
    this.raised = true;
    this.dialog.showModal();

    requestAnimationFrame(() => {
      this.input?.focus();
    });

    this.trackEvent('modal_displayed', { context: this.modalContext });
  }

  close() {
    if (!this.dialog) return;

    this.dialog.classList.add('animate-slide-out');

    setTimeout(() => {
      this.raised = false;
      this.dialog.close();
      this.dialog.classList.remove('animate-slide-out');
    }, 200);
  }

  open() {
    this.show();
  }

  onDialogClose() {
    this.raised = false;
    this.trackEvent('modal_closed', { context: this.modalContext });
  }

  reset() {
    if (this.input) this.input.value = '';
    this.clearError();
    if (this.submitButton) {
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Continue';
    }
    this.raised = false;
  }

  formatInput() {
    if (!this.input) return;
    let value = this.input.value.replace(/\D/g, '');
    if (value.length > 5) {
      value = value.substring(0, 5);
    }
    this.input.value = value;
  }

  validateZipCode(zipCode) {
    return /^\d{5}$/.test(zipCode);
  }

  showError(message = 'Please enter a valid 5-digit ZIP code.') {
    if (!this.input || !this.errorMessage) return;

    this.input.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-200');
    this.input.classList.remove('border-gray-200', 'focus:border-blue-500', 'focus:ring-blue-200');
    this.errorMessage.textContent = message;
    this.errorMessage.classList.remove('hidden');

    // Set aria-invalid for accessibility
    this.input.setAttribute('aria-invalid', 'true');
    this.input.setAttribute('aria-describedby', this.errorMessage.id);
  }

  clearError() {
    if (!this.input || !this.errorMessage) return;

    this.input.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-200');
    this.input.classList.add('border-gray-200', 'focus:border-blue-500', 'focus:ring-blue-200');
    this.errorMessage.classList.add('hidden');

    // Clear aria-invalid
    this.input.removeAttribute('aria-invalid');
    this.input.removeAttribute('aria-describedby');
  }

  async handleSubmit(event) {
    if (!this.input || !this.submitButton) return;

    const zipCode = this.input.value.trim();

    if (!this.validateZipCode(zipCode)) {
      this.showError();
      this.input.focus();
      return;
    }

    sessionStorage.setItem('customerZipCode', zipCode);
    pubsub.publish(Events.zipCodeCollected, zipCode);

    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Processing...';

    try {
      // Track successful submission
      this.trackEvent('zipcode_submitted', {
        zipCode,
        context: this.modalContext,
      });

      // Handle success
      this.handleSuccess(zipCode);
    } catch (error) {
      logger.error('Error submitting ZIP code:', error);
      this.showError('Something went wrong. Please try again.');
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Continue';
    }
  }

  handleSuccess(zipCode) {
    // Close modal
    this.close();

    // Dispatch custom event for other scripts to listen to
    window.dispatchEvent(
      new CustomEvent('zipCodeCollected', {
        detail: {
          zipCode,
          context: this.modalContext,
          timestamp: Date.now(),
        },
      })
    );
  }

  trackEvent(eventName, data = {}) {
    logger.log('ZIP Modal Event:', eventName, data);

    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'ZIP_Modal',
        event_label: data.context,
        custom_parameter_1: data.context,
        custom_parameter_2: data.zipCode,
      });
    }

    // Shopify Analytics
    if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.track) {
      ShopifyAnalytics.track(eventName, data);
    }

    // Custom analytics hook
    if (typeof window.customAnalytics !== 'undefined') {
      window.customAnalytics.track(eventName, data);
    }
  }

  /**
   * @returns {BranchLocation|null}
   */
  getStoredLocationInformation() {
    const data = sessionStorage.getItem('locationInformation');
    if (!data) return null;
    const thing = data;
    try {
      const parsed = JSON.parse(thing);
      return BranchLocation.fromJSON(parsed);
    } catch (e) {
      logger.error(e);
    }
  }

  shouldShowModal() {
    const stored = this.getStoredLocationInformation();
    const valid = 5 === stored?.zip?.length;
    return !valid;
  }

  // Static method to register the custom element
  static register() {
    if (!customElements.get('zip-code-modal')) {
      customElements.define('zip-code-modal', ZipCodeModal);
    }
  }
}

// Auto-register the custom element
ZipCodeModal.register();
